import React from 'react';
import { Link } from 'react-router-dom';
import { APP } from '../app/appConfig';
import type { DownloadFeature, DownloadIconKey, DownloadShot, DownloadTablet } from '../app/types';
import { usePlatform } from '../utils/usePlatform';
import { cn } from '../ui';
import { AppleIcon, PlayIcon } from './storeIcons';
import { DeviceFrame } from './download/DeviceFrame';

/**
 * `/download` — the page the app stores link back to, and the one page whose
 * whole job is to make someone want the app.
 *
 * It renders `APP.download` and holds no copy of its own. The two tenants ship
 * genuinely different products — bm is Sri Swamiji's own 2,000-bhajan library
 * with ragams, swaras and albums; ss is ~100 bhajans about Sri Swamiji by his
 * devotees, with no recordings — so every claim lives in that tenant's config,
 * next to a comment saying where the number came from.
 *
 * **No price appears anywhere, on purpose.** bm is paid and ss is free, both
 * can change, and the store listing is the only place a price is ever current.
 * An earlier version of this page hardcoded "Both apps are free"; that was
 * wrong for bm the day it shipped and nobody noticed. `Download.test.tsx`
 * fails the build if a price claim ever comes back.
 *
 * Both stores are always shown; the visitor's platform (detected client-side
 * by `usePlatform`) is surfaced first and marked. A wrong guess degrades to the
 * default (iOS first) order — never to a missing link.
 */

const Download: React.FC = () => {
  const platform = usePlatform();
  const stores = APP.storeUrls;
  const content = APP.download;
  const siteName = APP.brand.siteName;

  // features.appDownload gates the route, and a tenant that turns it on must
  // supply both. Guarding on the data rather than the flag keeps this a blank
  // sentence instead of a crash if the two ever drift apart.
  if (!stores || !content) {
    return (
      <main id="main" className="mx-auto max-w-3xl px-4 sm:px-6 py-16">
        <h1 className="text-h1 font-display font-semibold mb-6">Download</h1>
        <p className="text-body text-text-secondary">
          The {siteName} app is not available for download from this site.
        </p>
      </main>
    );
  }

  const iosFirst = platform !== 'android'; // 'ios' and 'unknown' both default iOS-first
  const cards: StoreCardProps[] = [
    { href: stores.ios, platform: 'ios', recommended: platform === 'ios' },
    { href: stores.android, platform: 'android', recommended: platform === 'android' },
  ];
  const ordered = iosFirst ? cards : [cards[1], cards[0]];
  const [hero, ...rest] = content.shots;

  return (
    <main id="main">
      {/* ── Hero ─────────────────────────────────────────────────────────── */}
      <section
        className="relative overflow-hidden border-b border-light"
        style={{ background: content.heroBackground }}
      >
        <div className="mx-auto max-w-7xl px-4 sm:px-6 py-14 sm:py-20">
          <div className="grid items-center gap-14 lg:grid-cols-[minmax(0,1fr)_330px] lg:gap-20 xl:gap-24">
            <div>
              <p className="mb-4 text-small uppercase tracking-[0.2em] text-accent-gold/80">
                {siteName} {content.eyebrow}
              </p>
              <h1 className="font-display font-semibold text-[clamp(2.25rem,6vw,3.5rem)] leading-[1.12] tracking-tight">
                {content.headline.lead}
                <br />
                <span className="text-accent-gold">{content.headline.accent}</span>
              </h1>
              <p className="mt-6 max-w-2xl text-body-lg leading-relaxed text-text-secondary">
                {content.lede}
              </p>

              <div className="mt-9 grid gap-3 sm:max-w-lg sm:grid-cols-2">
                {ordered.map((c) => (
                  <StoreCard key={c.platform} {...c} />
                ))}
              </div>

              <div className="mt-6 flex flex-wrap items-center gap-x-5 gap-y-2 text-small text-text-tertiary">
                {content.rating && (
                  <>
                    <span className="inline-flex items-center gap-1.5">
                      <Stars />
                      <span className="text-text-secondary">
                        {content.rating.score} {content.rating.source}
                      </span>
                    </span>
                    <span className="hidden sm:inline text-text-tertiary/50">·</span>
                  </>
                )}
                <span>iPhone, iPad, Mac and Android</span>
              </div>
            </div>

            {hero && <DeviceFrame src={publicUrl(hero.src)} alt={hero.alt} priority />}
          </div>
        </div>
      </section>

      {/* ── Stats ────────────────────────────────────────────────────────── */}
      <section className="border-b border-light bg-bg-2/30">
        <div className="mx-auto grid max-w-7xl grid-cols-2 gap-y-8 px-4 py-10 sm:px-6 md:grid-cols-4">
          {content.stats.map((s) => (
            <div key={s.label} className="px-2 text-center">
              <div className="font-display text-h1 font-semibold leading-none text-text-primary">
                {s.value}
              </div>
              <div className="mt-1.5 text-small uppercase tracking-[0.14em] text-text-tertiary">
                {s.label}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ── Features ─────────────────────────────────────────────────────── */}
      <section className="mx-auto max-w-7xl px-4 sm:px-6 py-16 sm:py-20">
        <header className="mb-10 max-w-2xl">
          <h2 className="font-display text-h2 font-semibold">{content.features.heading}</h2>
          <p className="mt-3 text-body-lg leading-relaxed text-text-secondary">
            {content.features.lede}
          </p>
        </header>
        <div className={cn('grid gap-4 sm:grid-cols-2', featureCols(content.features.items.length))}>
          {content.features.items.map((f) => (
            <FeatureCard key={f.title} {...f} />
          ))}
        </div>
      </section>

      {/* ── Screenshot gallery ───────────────────────────────────────────── */}
      {rest.length > 0 && <Gallery shots={rest} />}

      {/* ── iPad / Mac ───────────────────────────────────────────────────── */}
      <TabletBand tablet={content.tablet} />

      {/* ── Languages ────────────────────────────────────────────────────── */}
      <section className="border-y border-light bg-bg-2/30">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 py-14">
          <div className="flex flex-col gap-8 lg:flex-row lg:items-center lg:gap-14">
            <div className="lg:w-[32%] lg:shrink-0">
              <h2 className="font-display text-h2 font-semibold">{content.languages.heading}</h2>
              <p className="mt-3 text-body leading-relaxed text-text-secondary">
                {content.languages.body}
              </p>
            </div>
            <ul className="flex flex-wrap gap-2.5 lg:flex-1">
              {content.languages.endonyms.map((l) => (
                <li
                  key={l}
                  className="rounded-xl border border-light bg-bg-1 px-4 py-2.5 text-body text-text-primary transition-colors hover:border-accent-gold/50"
                >
                  {l}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* ── Together (bm only today) ─────────────────────────────────────── */}
      {content.together && (
        <section className="mx-auto max-w-7xl px-4 sm:px-6 py-16 sm:py-20">
          <header className="mb-10 max-w-2xl">
            <h2 className="font-display text-h2 font-semibold">{content.together.heading}</h2>
            <p className="mt-3 text-body-lg leading-relaxed text-text-secondary">
              {content.together.lede}
            </p>
          </header>
          <div className="grid gap-4 sm:grid-cols-3">
            {content.together.items.map((f) => (
              <FeatureCard key={f.title} {...f} />
            ))}
          </div>
        </section>
      )}

      {/* ── Closing CTA ──────────────────────────────────────────────────── */}
      <section className="border-t border-light">
        <div
          className="mx-auto max-w-7xl px-4 sm:px-6 py-16 sm:py-20"
          style={{ background: content.closingBackground }}
        >
          <div className="mx-auto max-w-2xl text-center">
            <h2 className="font-display text-h2 font-semibold">{content.closing.heading}</h2>
            <p className="mt-3 text-body-lg leading-relaxed text-text-secondary">
              {content.closing.body}
            </p>
            <div className="mx-auto mt-8 grid max-w-lg gap-3 sm:grid-cols-2">
              {ordered.map((c) => (
                <StoreCard key={c.platform} {...c} />
              ))}
            </div>
            <p className="mt-8 text-small text-text-tertiary">
              Not ready yet?{' '}
              <Link to="/bhajans" className="text-accent-gold underline-offset-4 hover:underline">
                Browse the library here
              </Link>{' '}
              — it is all on this site too.
            </p>
          </div>
        </div>
      </section>
    </main>
  );
};

/* ── Pieces ─────────────────────────────────────────────────────────────── */

/**
 * Screenshots live in `public/`, which CRA serves from PUBLIC_URL. Config
 * stores the bare path so it reads cleanly; the prefix is applied here, once.
 */
const publicUrl = (path: string) => `${process.env.PUBLIC_URL ?? ''}${path}`;

/**
 * Wide-screen column count for the feature grid, so the last row is never a
 * single stranded card: 3-up when the count divides by three (bm has 6), 2-up
 * otherwise (ss has 4, which reads as a clean 2×2). Both class strings are
 * literals so Tailwind still emits them.
 */
const featureCols = (count: number) => (count % 3 === 0 ? 'lg:grid-cols-3' : 'lg:grid-cols-2');

/**
 * The remaining screenshots. A scroll-snapping row on small screens (a 6-up
 * grid would shrink each phone to illegibility) and a plain grid from `md` up.
 */
const Gallery: React.FC<{ shots: DownloadShot[] }> = ({ shots }) => (
  <section className="border-t border-light bg-bg-2/20 py-14 sm:py-16">
    <div className="mx-auto max-w-7xl px-4 sm:px-6">
      <h2 className="mb-8 font-display text-h2 font-semibold">A look inside</h2>
    </div>
    {/* Centred wrapping row rather than a grid: the gallery holds however many
        shots a tenant has left over after the hero (bm 2, ss 4), and a fixed
        column count strands them against one edge. */}
    <ul
      className={cn(
        'flex snap-x snap-mandatory gap-5 overflow-x-auto px-4 pb-4 sm:px-6',
        'md:mx-auto md:max-w-7xl md:flex-wrap md:justify-center md:overflow-visible md:gap-8'
      )}
    >
      {shots.map((s) => (
        <li key={s.src} className="w-[210px] shrink-0 snap-start md:w-[220px]">
          <DeviceFrame src={publicUrl(s.src)} alt={s.alt} />
          <p className="mt-3 text-center text-small text-text-tertiary">{s.caption}</p>
        </li>
      ))}
    </ul>
  </section>
);

/**
 * The "also on iPad and Mac" band. Both apps are iOS builds that Apple lets
 * Apple-silicon Macs install directly (macOS 13.5+, M1 or later, per each
 * store listing), so one iPad screenshot carries both claims.
 */
const TabletBand: React.FC<{ tablet: DownloadTablet }> = ({ tablet }) => (
  <section className="border-t border-light">
    <div className="mx-auto max-w-7xl px-4 sm:px-6 py-14 sm:py-16">
      <div className="flex flex-col items-center gap-10 md:flex-row md:gap-14">
        <div className="md:w-[42%] md:shrink-0">
          <div className="overflow-hidden rounded-[1.5rem] border border-white/15 bg-[#0b0d16] p-[7px] shadow-4">
            <img
              src={publicUrl(tablet.src)}
              alt={tablet.alt}
              width={1032}
              height={1376}
              loading="lazy"
              decoding="async"
              className="block w-full rounded-[1.15rem]"
            />
          </div>
        </div>
        <div className="md:flex-1">
          <h2 className="font-display text-h2 font-semibold">{tablet.heading}</h2>
          <p className="mt-3 max-w-xl text-body-lg leading-relaxed text-text-secondary">
            {tablet.body}
          </p>
        </div>
      </div>
    </div>
  </section>
);

const FeatureCard: React.FC<DownloadFeature> = ({ icon, title, body }) => (
  <div className="group rounded-2xl border border-light bg-bg-2/50 p-6 transition-colors hover:border-accent-gold/40 hover:bg-bg-2">
    <div className="mb-4 inline-flex h-11 w-11 items-center justify-center rounded-xl bg-accent-gold/10 text-accent-gold transition-colors group-hover:bg-accent-gold/15">
      {ICONS[icon]}
    </div>
    <h3 className="mb-2 font-display text-h3 font-semibold leading-snug">{title}</h3>
    <p className="text-body leading-relaxed text-text-secondary">{body}</p>
  </div>
);

interface StoreCardProps {
  href: string;
  platform: 'ios' | 'android';
  /** True when this card matches the visitor's detected platform. */
  recommended: boolean;
}

const STORE_COPY = {
  ios: { badgeTop: 'Download on the', badgeBottom: 'App Store', label: 'iOS' },
  android: { badgeTop: 'Get it on', badgeBottom: 'Google Play', label: 'Android' },
} as const;

const StoreCard: React.FC<StoreCardProps> = ({ href, platform, recommended }) => {
  const copy = STORE_COPY[platform];
  const siteName = APP.brand.siteName;
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      aria-label={`${copy.badgeTop} ${copy.badgeBottom} — ${siteName} for ${copy.label}`}
      className={cn(
        'group relative flex h-16 items-center gap-3 rounded-2xl border px-5 transition-all duration-150 ease-out-quint',
        recommended
          ? 'border-accent-gold bg-accent-gold/10 ring-1 ring-accent-gold/40 hover:bg-accent-gold/15'
          : 'border-light bg-bg-2/70 hover:border-strong hover:bg-bg-3'
      )}
    >
      {recommended && (
        <span className="absolute -top-2.5 right-4 rounded-full bg-accent-gold px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-bg-0">
          For your device
        </span>
      )}
      {platform === 'ios' ? <AppleIcon size={26} /> : <PlayIcon size={26} />}
      <span className="flex min-w-0 flex-col leading-tight">
        <span className="text-[10px] uppercase tracking-wide text-text-tertiary">
          {copy.badgeTop}
        </span>
        <span className="truncate text-body font-semibold">{copy.badgeBottom}</span>
      </span>
      <span className="ml-auto text-text-tertiary transition-transform duration-150 group-hover:translate-x-0.5">
        <Arrow />
      </span>
    </a>
  );
};

const Stars = () => (
  <span className="inline-flex gap-0.5 text-accent-gold" aria-hidden>
    {[0, 1, 2, 3, 4].map((i) => (
      <svg key={i} width="13" height="13" viewBox="0 0 24 24" fill="currentColor">
        <path d="M12 2l2.9 6.26 6.85.72-5.1 4.6 1.44 6.72L12 16.9l-6.09 3.4 1.44-6.72-5.1-4.6 6.85-.72z" />
      </svg>
    ))}
  </span>
);

const Arrow = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="M5 12h14M13 6l6 6-6 6" />
  </svg>
);

/* Feature glyphs — 22px, 1.6 stroke, currentColor. Keyed by DownloadIconKey so
   a config key with no glyph fails to compile rather than rendering a blank. */
const glyph = {
  width: 22,
  height: 22,
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.6,
  strokeLinecap: 'round',
  strokeLinejoin: 'round',
  'aria-hidden': true,
} as const;

const ICONS: Record<DownloadIconKey, React.ReactNode> = {
  offline: (
    <svg {...glyph}>
      <path d="M3 3l18 18" />
      <path d="M18.9 16.5A4 4 0 0 0 17 9h-1.3A7 7 0 0 0 8.4 5.6" />
      <path d="M6.6 6.7A7 7 0 0 0 5 9.7 4 4 0 0 0 6 17.5h10" />
    </svg>
  ),
  script: (
    <svg {...glyph}>
      <path d="M4 5h9M8.5 5v3.5c0 3.5-2 5.8-4.5 6.8" />
      <path d="M7 11.5c1.3 2.2 3 3.6 5 4.3" />
      <path d="M13 20l4-9 4 9" />
      <path d="M14.4 17h5.2" />
    </svg>
  ),
  wave: (
    <svg {...glyph}>
      <path d="M3 12v1M7 8v8M11 4.5v15M15 8v8M19 11v2M21.5 12.5v-1" />
    </svg>
  ),
  notes: (
    <svg {...glyph}>
      <path d="M4 6h16M4 12h16M4 18h16" opacity="0.35" />
      <circle cx="8.5" cy="15" r="2" />
      <path d="M10.5 15V5.5l7-1.5V13" />
      <circle cx="15.5" cy="13" r="2" />
    </svg>
  ),
  tuning: (
    <svg {...glyph}>
      <circle cx="12" cy="12" r="9" />
      <path d="M12 3v4M12 17v4M3 12h4M17 12h4" />
      <circle cx="12" cy="12" r="2.5" />
    </svg>
  ),
  search: (
    <svg {...glyph}>
      <circle cx="11" cy="11" r="6.5" />
      <path d="M16 16l4.5 4.5" />
      <path d="M9 11h4M11 9v4" opacity="0.5" />
    </svg>
  ),
  broadcast: (
    <svg {...glyph}>
      <circle cx="12" cy="12" r="2.5" />
      <path d="M7.8 7.8a6 6 0 0 0 0 8.4M16.2 16.2a6 6 0 0 0 0-8.4" />
      <path d="M4.9 4.9a10 10 0 0 0 0 14.2M19.1 19.1a10 10 0 0 0 0-14.2" opacity="0.5" />
    </svg>
  ),
  list: (
    <svg {...glyph}>
      <path d="M4 7h11M4 12h11M4 17h7" />
      <path d="M18.5 10.5V18" />
      <circle cx="16.8" cy="18.3" r="1.7" />
    </svg>
  ),
  star: (
    <svg {...glyph}>
      <path d="M12 3.5l2.6 5.5 5.9.7-4.4 4 1.2 5.8L12 16.6 6.7 19.5l1.2-5.8-4.4-4 5.9-.7z" />
    </svg>
  ),
};

export default Download;
